import {inject, Injectable} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CommunicationCenterService } from '@modules/communication-center';
import { DynamicNavigationService } from '../../../../navigation/dynamic-navigation.service';
import { AuthenticationService } from '@modules/authentication';
import { AuthorizationService } from '@modules/authorization';
import { AssignationAuthorizationService } from './assignation-authorization.service';
import { AssignationService } from './assignation.service';
import { DataEntity } from 'octopus-connect';
import { AsyncRules } from '../models/rules';
import { WidgetAssignmentsLessonComponent } from '../components/widget/widget-assignments-lesson/widget-assignments-lesson.component';
import { WidgetAssignmentsLearnersWithoutActiveAssignmentComponent } from '../components/widget/widget-assignments-learners-without-active-assignment/widget-assignments-learners-without-active-assignment.component';
import {AssignmentComponentsService} from '@modules/assignation/core/services/assignment-components.service';
import {OnboardingService} from '@modules/assignation/core/onboarding/onboarding.service';
import {ContextualService} from '@modules/assignation/core/services/contextual.service';

@Injectable({
  providedIn: 'root' // Fourni à la racine pour être disponible dès le démarrage
})
export class AssignationInitializerService {
    private static isMenuSet = false;
    private onLogout$: Subject<void> = new Subject<void>();

    constructor(
        private dynamicNavigation: DynamicNavigationService,
        private authService: AuthenticationService,
        private authorization: AuthorizationService,
        private assignationAuthorizationService: AssignationAuthorizationService,
        private communicationCenter: CommunicationCenterService,
        private assignationService: AssignationService,
        private router: Router,
        private onboardingService: OnboardingService,
        private contextualActions: ContextualService,
        private assignmentComponentsService: AssignmentComponentsService,
        ) {

    }

    initialize(): void {
        this.assignationAuthorizationService.activeRulesOnstartUp();

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: DataEntity) => {
                if (data) {
                    this.postAuthentication();
                } else {
                    this.postLogout();
                }
            });

        this.registerNotifications();
        this.registerWidgets();
    }

    private registerNotifications(): void {
        this.communicationCenter
            .getRoom('notifications')
            .getSubject('registerNotification')
            .next({
                name: 'Assignment',
                type: 'NEW_ASSIGNATION',
                icon: 'activity',
                text: 'assignment.notification_new_assignment',
                textTransform: (text: string, data: Object) => {
                    return {
                        userName: data['userName'],
                        formName: data['formName'],
                    };
                },
                action: (data: Object) => {
                    this.assignationService.loadAndGetAssignmentById(data['id']).pipe(
                        take(1))
                        .subscribe((assignment: DataEntity) => {
                            this.assignationService.launchAssignment(assignment);

                            const node = assignment.get('assignated_node');
                            const route = [];

                            if (data['project']) {
                                route.push(...['projects', data['project'], 'tools']);
                            }

                            // Adaptez la route pour le lazy loading
                            route.push(...['followed', 'assignment', `${node.type}s`, node.id, 'player']);

                            this.router.navigate(route);
                        });
                }
            });
    }

    private registerWidgets(): void {
         this.communicationCenter
            .getRoom('assignation')
            .next('widgetAssignmentsLessonComponent', WidgetAssignmentsLessonComponent);

        this.communicationCenter
            .getRoom('assignation')
            .next('WidgetAssignmentsLearnersWithoutActiveAssignmentComponent', WidgetAssignmentsLearnersWithoutActiveAssignmentComponent);
    }


    private postLogout(): void {
        AssignationInitializerService.isMenuSet = false;
        this.dynamicNavigation.clearMenuItem('level0', 'followed');
        this.dynamicNavigation.clearMenuItem('level0', 'followed-tab');
        this.onLogout$.next();
        this.onLogout$.complete();
    }

    private postAuthentication(): void {
        this.onLogout$ = new Subject<void>();

        if (!AssignationInitializerService.isMenuSet) {
            this.authorization.currentUserCan<Observable<boolean>>(AsyncRules.ReadAssignments)
                .pipe(takeUntil(this.onLogout$))
                .subscribe((isAuthorized) => {
                    if (isAuthorized) {
                        this.dynamicNavigation.registerModuleMenu('level0', {
                            'id': 'followed',
                            'title': 'Assignations',
                            'translate': 'assignment.title',
                            'type': 'item',
                            'icon': 'assignment',
                            'url': '/followed' // Chemin de la route lazy-loadée
                        });

                        // Potentiellement redondant si /followed redirige vers /followed/tab, à vérifier
                        this.dynamicNavigation.registerModuleMenu('level0', {
                            'id': 'followed-tab',
                            'title': 'Assignations',
                            'translate': 'assignment.title',
                            'type': 'item',
                            'icon': 'assignment',
                            'url': '/followed/tab' // Chemin de la sous-route
                        });
                    } else {
                        this.dynamicNavigation.clearMenuItem('level0', 'followed');
                        this.dynamicNavigation.clearMenuItem('level0', 'followed-tab');
                    }

                    AssignationInitializerService.isMenuSet = true;
                });
        }
    }
}